import { useAuth } from '../../contexts/AuthContext';
import { CreditCard, Download, CheckCircle, Shield, Award, User, AlertCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { LoadingSpinner } from '../UI/Card';
import QRCode from 'qrcode';

export function DigitalID() {
  const { profile, profileLoading, profileError, refreshProfile } = useAuth();
  const qrContainerRef = useRef<HTMLDivElement>(null);
  const [qrError, setQrError] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [qrGenerated, setQrGenerated] = useState(false);

  useEffect(() => {
    if (profile && qrContainerRef.current && isFlipped && !qrGenerated) {
      generateQRCode();
    }
  }, [profile, isFlipped, qrGenerated]);

  const generateQRCode = async () => {
    if (!profile || !qrContainerRef.current) return;

    try {
      const verificationData = JSON.stringify({
        id: profile.id,
        name: profile.full_name,
        type: profile.user_type,
        verified: profile.id_verified,
        email: profile.email,
        timestamp: Date.now(),
      });

      const canvas = document.createElement('canvas');
      await QRCode.toCanvas(canvas, verificationData, {
        width: 200,
        margin: 2,
        color: {
          dark: '#1e40af',
          light: '#ffffff',
        },
      });

      qrContainerRef.current.innerHTML = '';
      qrContainerRef.current.appendChild(canvas);
      setQrError(false);
      setQrGenerated(true);
    } catch (error) {
      console.error('QR Code generation error:', error);
      setQrError(true);
    }
  };

  const handleDownload = async () => {
    if (!profile) return;

    const downloadCanvas = document.createElement('canvas');
    const ctx = downloadCanvas.getContext('2d');
    if (!ctx) return;

    downloadCanvas.width = 1000;
    downloadCanvas.height = 630;

    const gradient = ctx.createLinearGradient(0, 0, downloadCanvas.width, 0);
    gradient.addColorStop(0, '#2563eb');
    gradient.addColorStop(1, '#06b6d4');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
    for (let i = 0; i < 20; i++) {
      ctx.beginPath();
      ctx.arc(
        Math.random() * downloadCanvas.width,
        Math.random() * downloadCanvas.height,
        Math.random() * 100,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }

    if (profile.avatar_url) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = profile.avatar_url;
        await new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
        if (img.complete) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(100, 150, 60, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(img, 40, 90, 120, 120);
          ctx.restore();
        }
      } catch (e) {
        console.error('Avatar loading error:', e);
      }
    } else {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.beginPath();
      ctx.arc(100, 150, 60, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.fillStyle = 'white';
    ctx.font = 'bold 48px Arial';
    ctx.fillText('CommunityPerks', 60, 70);

    ctx.font = 'bold 36px Arial';
    ctx.fillText(profile.full_name, 60, 280);

    ctx.font = '24px Arial';
    ctx.fillText(`${profile.user_type.toUpperCase()} MEMBER`, 60, 320);

    ctx.font = '20px Arial';
    ctx.fillText(`ID: ${profile.id.substring(0, 8).toUpperCase()}`, 60, 360);

    if (profile.organization) {
      ctx.fillText(profile.organization, 60, 395);
    }

    if (profile.university) {
      ctx.fillText(profile.university, 60, 430);
    }

    if (profile.company) {
      ctx.fillText(profile.company, 60, 430);
    }

    ctx.font = '18px Arial';
    ctx.fillText(`Member since ${new Date(profile.created_at).getFullYear()}`, 60, 470);

    if (profile.id_verified) {
      ctx.fillStyle = '#10b981';
      ctx.fillRect(60, 500, 180, 40);
      ctx.fillStyle = 'white';
      ctx.font = 'bold 20px Arial';
      ctx.fillText('✓ VERIFIED', 85, 527);
    }

    const verificationData = JSON.stringify({
      id: profile.id,
      name: profile.full_name,
      type: profile.user_type,
      verified: profile.id_verified,
      timestamp: Date.now(),
    });

    try {
      const qrCanvas = document.createElement('canvas');
      await QRCode.toCanvas(qrCanvas, verificationData, {
        width: 250,
        margin: 1,
        color: {
          dark: '#ffffff',
          light: '#00000000',
        },
      });

      ctx.drawImage(qrCanvas, 700, 180, 250, 250);

      ctx.fillStyle = 'white';
      ctx.font = '18px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Scan to verify', 825, 460);
      ctx.textAlign = 'left';
    } catch (error) {
      console.error('QR generation error:', error);
    }

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 2;
    ctx.strokeRect(10, 10, downloadCanvas.width - 20, downloadCanvas.height - 20);

    const link = document.createElement('a');
    link.download = `${profile.full_name.replace(/\s+/g, '_')}_CommunityID.png`;
    link.href = downloadCanvas.toDataURL('image/png');
    link.click();
  };

  if (profileLoading) {
    return <LoadingSpinner />;
  }

  if (profileError || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="glass-card max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-950/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Unable to Load PLIP ID</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {profileError || 'Profile not found. Please try again.'}
          </p>
          <button
            onClick={refreshProfile}
            className="glass-button w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-blue-600 transition-all duration-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const expiryDate = profile.user_type === 'student'
    ? new Date(new Date(profile.created_at).setFullYear(new Date(profile.created_at).getFullYear() + 1))
    : null;

  return (
    <div className="min-h-screen">
      <div className="glass-card bg-gradient-to-r from-indigo-600 to-blue-500 text-white py-12 mx-4 mt-4 mb-6 shadow-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-4">
            <CreditCard className="w-8 h-8" />
            <h1 className="text-3xl md:text-4xl font-bold">PLIP ID Card</h1>
          </div>
          <p className="text-indigo-100 text-lg">
            Your verified community identification card
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="relative" style={{ perspective: '2000px', minHeight: '550px' }}>
          <div
            className={`relative w-full transition-all duration-700 cursor-pointer`}
            style={{
              transformStyle: 'preserve-3d',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
            onClick={() => setIsFlipped(!isFlipped)}
          >
            {/* Front of Card */}
            <div
              className="glass-card rounded-2xl shadow-2xl overflow-hidden absolute top-0 left-0 w-full"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
              }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950/30 dark:to-blue-950/30 opacity-50"></div>
                <div className="relative bg-gradient-to-r from-indigo-600 to-blue-500 p-8 md:p-12">
                  <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
                    <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full"></div>
                    <div className="absolute bottom-10 right-10 w-60 h-60 bg-white rounded-full"></div>
                  </div>

                  <div className="relative text-white">
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center space-x-3">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border-2 border-white/30">
                        <CreditCard className="w-7 h-7" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold">CommunityPerks</h2>
                        <p className="text-blue-100 text-sm">Digital Identity Card</p>
                      </div>
                    </div>
                  </div>

                    <div className="flex items-start space-x-6 mb-6">
                      {profile.avatar_url ? (
                        <img
                          src={profile.avatar_url}
                          alt={profile.full_name}
                          className="w-24 h-24 rounded-2xl border-4 border-white/30 object-cover shadow-lg"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-2xl border-4 border-white/30 bg-white/20 flex items-center justify-center shadow-lg">
                          <User className="w-12 h-12" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-indigo-100 text-sm mb-1">Cardholder Name</p>
                        <p className="text-3xl font-bold mb-3">{profile.full_name}</p>
                        {profile.id_verified && (
                          <div className="inline-flex items-center space-x-2 bg-green-500 px-3 py-1 rounded-full">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-sm font-semibold">Verified Member</span>
                          </div>
                        )}
                      </div>
                    </div>

                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div>
                      <p className="text-indigo-100 text-sm mb-1">Member Type</p>
                      <p className="text-lg font-bold uppercase tracking-wide">{profile.user_type}</p>
                    </div>
                    <div>
                      <p className="text-indigo-100 text-sm mb-1">Member ID</p>
                      <p className="font-mono text-lg font-bold">{profile.id.substring(0, 12).toUpperCase()}</p>
                    </div>
                  </div>

                  {(profile.organization || profile.university || profile.company) && (
                    <div className="mb-4">
                      <p className="text-indigo-100 text-sm mb-1">Affiliated With</p>
                      <p className="text-lg font-semibold">
                        {profile.university || profile.company || profile.organization}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-indigo-100 text-sm mb-1">Member Since</p>
                      <p className="font-semibold">
                        {new Date(profile.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {expiryDate && (
                      <div>
                        <p className="text-indigo-100 text-sm mb-1">Valid Until</p>
                        <p className="font-semibold">{expiryDate.toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>

                    {profile.verification_type && (
                      <div className="mt-6 inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/30">
                        <Award className="w-5 h-5" />
                        <span className="font-semibold">{profile.verification_type}</span>
                      </div>
                    )}

                    <div className="mt-8 pt-6 border-t border-white/20">
                      <p className="text-indigo-100 text-sm text-center animate-pulse">
                        Tap card to reveal QR code
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Back of Card */}
            <div
              className="glass-card rounded-2xl shadow-2xl overflow-hidden absolute top-0 left-0 w-full"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
              }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 opacity-50"></div>
                <div className="relative bg-gradient-to-r from-blue-500 to-indigo-600 p-8 md:p-12 min-h-[500px] flex flex-col items-center justify-center">
                  <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
                    <div className="absolute top-10 right-10 w-40 h-40 bg-white rounded-full"></div>
                    <div className="absolute bottom-10 left-10 w-60 h-60 bg-white rounded-full"></div>
                  </div>

                  <div className="relative text-white text-center">
                    <div className="mb-8">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border-2 border-white/30 mx-auto mb-4">
                        <CreditCard className="w-7 h-7" />
                      </div>
                      <h2 className="text-2xl font-bold mb-2">Verification QR Code</h2>
                      <p className="text-blue-100">Scan to verify this ID</p>
                    </div>

                    <div className="bg-white p-6 rounded-2xl shadow-2xl border-4 border-white/50 mb-6">
                      {!qrError ? (
                        <div
                          ref={qrContainerRef}
                          className="w-64 h-64 flex items-center justify-center"
                        >
                          {!qrGenerated && (
                            <div className="flex flex-col items-center">
                              <div className="loading-spinner mb-2"></div>
                              <p className="text-gray-500 text-sm">Generating QR...</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-64 h-64 flex items-center justify-center bg-gray-100 rounded-lg">
                          <p className="text-gray-500 text-sm text-center">QR Code<br/>Unavailable</p>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <p className="text-lg font-semibold">Member ID: {profile.id.substring(0, 12).toUpperCase()}</p>
                      <p className="text-blue-100 text-sm">This QR code contains your encrypted verification data</p>
                      <div className="mt-6 pt-4 border-t border-white/20">
                        <p className="text-indigo-100 text-sm animate-pulse">
                          Tap card to return to front
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card rounded-2xl shadow-lg overflow-hidden mt-6">
          <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-900">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  <span>Your PLIP ID</span>
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Tap the card above to reveal the QR code for verification
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                className="glass-button flex items-center justify-center space-x-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white px-8 py-4 rounded-xl font-semibold hover:from-indigo-700 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Download className="w-5 h-5" />
                <span>Download ID Card</span>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="glass-card p-6 group hover:shadow-lg transition-shadow duration-300">
            <div className="w-14 h-14 bg-indigo-100 dark:bg-indigo-950/30 rounded-2xl flex items-center justify-center mb-4">
              <CreditCard className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Show & Save</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
              Present your digital ID at participating businesses to claim discounts and special offers
            </p>
          </div>

          <div className="glass-card p-6 group hover:shadow-lg transition-shadow duration-300">
            <div className="w-14 h-14 bg-green-100 dark:bg-green-950/30 rounded-2xl flex items-center justify-center mb-4">
              <CheckCircle className="w-7 h-7 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Get Verified</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
              Verify your account to unlock premium benefits, exclusive offers, and enhanced community trust
            </p>
          </div>

          <div className="glass-card p-6 group hover:shadow-lg transition-shadow duration-300">
            <div className="w-14 h-14 bg-blue-100 dark:bg-blue-950/30 rounded-2xl flex items-center justify-center mb-4">
              <Download className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Always Available</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
              Download your ID card or save it to your phone's wallet for quick offline access
            </p>
          </div>
        </div>

        {profile.user_type === 'student' && expiryDate && (
          <div className="glass-card mt-6 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-900 dark:text-yellow-300 mb-1">Student ID Expiration</h4>
                <p className="text-sm text-yellow-800 dark:text-yellow-400">
                  Student IDs are valid for one year from registration. Your ID will expire on{' '}
                  <span className="font-semibold">{expiryDate.toLocaleDateString()}</span>.
                  You'll need to reverify your student status to continue accessing student-only perks.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
